import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { sanitizeInput } from '../../utils/inputValidation';
import {
  getPrendasListing,
  getPrendasInformation,
  resetPrendaCase,
} from '../../services/prendasService';
import HondaLoader from '../../components/common/HondaLoader';
import Swal from 'sweetalert2';

const ControlPrendas = ({ user, onLogout, onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    row: null,
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatearFecha = (fecha) => {
    if (!fecha || fecha === 'N/A') return 'N/A';

    try {
      if (fecha.includes('T')) {
        fecha = fecha.split('T')[0];
      }

      if (fecha.includes('-')) {
        const partes = fecha.split('-');

        if (partes.length === 3 && partes[0].length === 4) {
          return `${partes[2]}-${partes[1]}-${partes[0]}`;
        }
      }

      return fecha;
    } catch {
      return fecha;
    }
  };

  const cargarPrendas = useCallback(async () => {
    try {
      setLoading(true);

      const rechazados = await getPrendasListing('RECHAZADO', user.ce_ID);

      const mapped = rechazados.map((item, idx) => ({
        id: item.id || idx + 1,
        group: item.group || item.grupo || 'N/A',
        order: item.order || item.orden || 'N/A',
        titular: item.titular || item.mainName || 'N/A',
        ident: item.ident || item.documentNumber || 'N/A',
        sellerHouse:
          item.sellerHouse?.ceName ||
          item.concesionario ||
          'N/A',
        vehiculeTag: item.vehiculeTag || 'N/A',
        recordDate: formatearFecha(item.recordDate || 'N/A'),
        registerName: item.registerName || '',
      }));

      setData(mapped);
    } catch (err) {
      console.error('Error cargando prendas rechazadas:', err);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar las prendas.',
        confirmButtonColor: '#ef4444',
      });

      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarPrendas();
  }, [cargarPrendas]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isDropdownOpen &&
        !e.target.closest('.dropdown-container')
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );
    };
  }, [isDropdownOpen]);

  const filteredData = data.filter((row) => {
    const s = searchTerm.toLowerCase();

    return (
      String(row.group).toLowerCase().includes(s) ||
      String(row.order).toLowerCase().includes(s) ||
      String(row.titular).toLowerCase().includes(s) ||
      String(row.ident).toLowerCase().includes(s) ||
      String(row.sellerHouse).toLowerCase().includes(s)
    );
  });

  // Azul: Ver detalle
  const handleVerDetalle = useCallback(
    (row) => {
      const prendaData = {
        group: row.group,
        order: row.order,
        titular: row.titular,
        ident: row.ident,
        sellerHouse: row.sellerHouse,
        recordDate: row.recordDate,
        vehiculeTag: row.vehiculeTag,
        registerName: row.registerName,
        id: row.id,
        fromDisponibles: false,
      };

      // Guardamos la información para que VisualizacionPrenda
      // pueda recuperar los datos de la prenda.
      localStorage.setItem(
        'prendaData',
        JSON.stringify(prendaData)
      );

      // Pasamos grupo y orden igual que en BusquedaPrendas.
      onNavigate('visualizacionPrenda', {
        group: row.group,
        order: row.order,
      });
    },
    [onNavigate]
  );

  // Naranja: Deshacer
  const handleDeshacerClick = useCallback((row) => {
    setModalConfig({
      isOpen: true,
      row,
    });
  }, []);

  const handleConfirmarDeshacer = useCallback(async () => {
    const { row } = modalConfig;

    setModalConfig({
      isOpen: false,
      row: null,
    });

    try {
      setLoading(true);

      await resetPrendaCase(
        row.group,
        row.order
      );

      await Swal.fire({
        icon: 'success',
        title: 'Prenda reseteada',
        text: 'La prenda fue reseteada exitosamente.',
        confirmButtonColor: '#ef4444',
      });

      await cargarPrendas();
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message,
        confirmButtonColor: '#ef4444',
      });

      setLoading(false);
    }
  }, [modalConfig, cargarPrendas]);

  // Verde: Recargar documentación
  const handleRecargarDoc = useCallback(
    async (row) => {
      try {
        setLoading(true);

        const stored = await getPrendasInformation(
          row.group,
          row.order,
          user.ce_ID
        );

        localStorage.setItem(
          'prendaData',
          JSON.stringify({
            group: row.group,
            order: row.order,
            titular: row.titular,
            ident: row.ident,
            id: row.id,
            recordDate:
              stored?.recordDate ||
              row.recordDate ||
              '',
            registerName:
              stored?.registerName ||
              row.registerName ||
              '',
            fromControl: true,
          })
        );

        onNavigate('agregarPrenda');
      } catch (err) {
        console.error(
          'Error obteniendo información de la prenda:',
          err
        );

        localStorage.setItem(
          'prendaData',
          JSON.stringify({
            group: row.group,
            order: row.order,
            titular: row.titular,
            ident: row.ident,
            id: row.id,
            recordDate: row.recordDate || '',
            registerName: row.registerName || '',
            fromControl: true,
          })
        );

        onNavigate('agregarPrenda');
      } finally {
        setLoading(false);
      }
    },
    [onNavigate]
  );

  return (
    <MainLayout
      user={user}
      onLogout={onLogout}
      onNavigate={onNavigate}
    >
      <div className="max-w-full mx-auto px-3 py-4">

        {/* Encabezado */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>

            <h2 className="text-2xl font-bold text-gradient">
              Control de Prendas
            </h2>
          </div>

          <p className="text-gray-500 text-sm ml-5">
            Gestione las prendas rechazadas
          </p>
        </div>

        {/* Filtros */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Buscador */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Buscar
              </label>

              <div className="relative">
                <span className="material-icons absolute left-3 top-2.5 text-gray-400 text-sm">
                  search
                </span>

                <input
                  type="text"
                  placeholder="Buscar por grupo, orden, titular..."
                  value={searchTerm}
                  onChange={(e) =>
                    setSearchTerm(
                      sanitizeInput(e.target.value)
                    )
                  }
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                />
              </div>
            </div>

            {/* Filtro */}
            <div className="flex items-end">
              <div className="relative dropdown-container">
                <button
                  onClick={() =>
                    setIsDropdownOpen(!isDropdownOpen)
                  }
                  className="flex items-center justify-between gap-2 px-3 xl:px-4 py-2 bg-white border border-gray-200 rounded-md text-xs xl:text-sm text-gray-700 hover:bg-gray-50 transition min-w-[160px]"
                >
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    Rechazados
                  </span>

                  <span className="material-icons text-gray-400 text-base">
                    {isDropdownOpen
                      ? 'expand_less'
                      : 'expand_more'}
                  </span>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-10 overflow-hidden">
                    <button
                      onClick={() =>
                        setIsDropdownOpen(false)
                      }
                      className="w-full text-left px-4 py-2 text-sm bg-red-50 text-red-600 transition"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                        Rechazados
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12">
            <HondaLoader
              message="Cargando prendas rechazadas..."
              variant="inline"
              size="large"
              subtitle="Obteniendo datos del servidor"
            />
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">

                {/* Encabezado */}
                <thead className="bg-gradient-to-r from-red-600 to-red-700">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Grupo
                    </th>

                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Orden
                    </th>

                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Titular
                    </th>

                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      DNI/CUIT
                    </th>

                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Concesionario
                    </th>

                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      F. Creación de Prenda
                    </th>

                    <th className="px-4 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-12 text-center text-gray-500"
                      >
                        <span className="material-icons text-4xl text-gray-300 block mb-2">
                          assignment_turned_in
                        </span>

                        {searchTerm
                          ? 'No se encontraron resultados para la búsqueda'
                          : 'No hay prendas rechazadas en este momento'}
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((row) => (
                      <tr
                        key={row.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {row.group}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900">
                          {row.order}
                        </td>

                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {row.titular}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900">
                          {row.ident}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900">
                          {row.sellerHouse}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-700">
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                            {row.recordDate}
                          </span>
                        </td>

                        {/* Acciones */}
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">

                            {/* Ver detalle */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleVerDetalle(row);
                              }}
                              className="text-blue-600 hover:bg-blue-600 hover:text-white transition-colors p-1 rounded-full"
                              title="Ver detalle"
                            >
                              <span className="material-icons text-xl">
                                visibility
                              </span>
                            </button>

                            {/* Deshacer */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeshacerClick(row);
                              }}
                              className="text-red-600 hover:bg-red-600 hover:text-white transition-colors p-1 rounded-full"
                              title="Deshacer prenda"
                            >
                              <span className="material-icons text-xl">
                                undo
                              </span>
                            </button>

                            {/* Recargar documentación */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRecargarDoc(row);
                              }}
                              className="text-green-600 hover:bg-green-600 hover:text-white transition-colors p-1 rounded-full"
                              title="Recargar documentación"
                            >
                              <span className="material-icons text-xl">
                                arrow_forward
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {filteredData.length > 0 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                Mostrando {filteredData.length} de {data.length}{' '}
                prendas rechazadas
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Deshacer */}
      {modalConfig.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">

            <div className="px-6 py-4 bg-gradient-to-r from-red-600 to-red-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/20">
                  <span className="material-icons text-2xl text-white">
                    undo
                  </span>
                </div>

                <h3 className="text-lg font-semibold text-white">
                  Deshacer Prenda
                </h3>
              </div>
            </div>

            <div className="px-6 py-5">
              <p className="text-gray-700 mb-4">
                ¿Está seguro que desea deshacer esta prenda?
              </p>

              {modalConfig.row && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Titular:
                    </span>

                    <span className="text-sm text-gray-800 font-semibold">
                      {modalConfig.row.titular}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Grupo:
                    </span>

                    <span className="text-sm text-gray-800">
                      {modalConfig.row.group}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Orden:
                    </span>

                    <span className="text-sm text-gray-800">
                      {modalConfig.row.order}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() =>
                  setModalConfig({
                    isOpen: false,
                    row: null,
                  })
                }
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>

              <button
                onClick={handleConfirmarDeshacer}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <span className="material-icons text-sm">
                  undo
                </span>

                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ControlPrendas;